import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "sonner";
import { devError } from "@/lib/logger";

const today = () => new Date().toISOString().slice(0, 10);

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

/**
 * Export tabular data as an Excel (.xlsx) workbook.
 * sheets: [{ name, rows: [["Header1","Header2"], ["a","b"], ...] }]
 * Uses explicit Blob + anchor download (works in all browsers; doesn't rely on
 * xlsx's writeFile, which can fail silently behind CSP / popup blockers).
 */
export function exportExcel(filename, sheets) {
  try {
    const safe = (sheets && sheets.length) ? sheets : [{ name: "Sheet1", rows: [["No data"]] }];
    const wb = XLSX.utils.book_new();
    const used = new Set();
    safe.forEach((s, idx) => {
      const rows = Array.isArray(s.rows) ? s.rows : [];
      const ws = XLSX.utils.aoa_to_sheet(rows.length ? rows : [[""]]);
      // Excel sheet name: max 31 chars, no special chars, must be unique
      let name = (s.name || `Sheet${idx + 1}`)
        .replace(/[\\/?*[\]:]/g, " ")
        .slice(0, 31)
        .trim() || `Sheet${idx + 1}`;
      let unique = name;
      let n = 2;
      while (used.has(unique.toLowerCase())) {
        const suffix = ` (${n++})`;
        unique = name.slice(0, 31 - suffix.length) + suffix;
      }
      used.add(unique.toLowerCase());
      XLSX.utils.book_append_sheet(wb, ws, unique);
    });

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    triggerDownload(blob, `${filename}-${today()}.xlsx`);
  } catch (err) {
    devError("Excel export failed", err);
    toast.error(`Excel export failed: ${err?.message || err}`);
  }
}

/**
 * Export tabular data as a PDF using jsPDF + autoTable.
 * sections: [{ title?, headers: ["H1","H2"], rows: [["a","b"]], foot?: [["Total","100"]] }]
 */
export function exportPDF(filename, title, sections, meta = {}) {
  try {
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();

  // Header
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(_sanitizeForPdf(meta.company || "AXIORA"), 40, 40);
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.text(_sanitizeForPdf(title), 40, 58);
  doc.setFontSize(9);
  doc.setTextColor(100);
  const metaLine = [meta.address, meta.gstin ? `GSTIN: ${meta.gstin}` : null,
    meta.date ? `As on ${meta.date}` : `Generated ${today()}`]
    .filter(Boolean).join("  -  ");
  doc.text(_sanitizeForPdf(metaLine), 40, 74);
  doc.setTextColor(0);
  doc.setDrawColor(15, 23, 42);
  doc.line(40, 82, pageWidth - 40, 82);

  let cursorY = 96;

  sections.forEach((sec) => {
    if (sec.title) {
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(_sanitizeForPdf(sec.title), 40, cursorY);
      cursorY += 6;
    }
    autoTable(doc, {
      startY: cursorY,
      head: [sec.headers.map(_sanitizeForPdf)],
      body: sec.rows.map((row) => row.map((c) => (c == null ? "" : _sanitizeForPdf(c)))),
      foot: sec.foot ? sec.foot.map((row) => row.map((c) => (c == null ? "" : _sanitizeForPdf(c)))) : undefined,
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [29, 78, 216], textColor: 255, fontStyle: "bold" },
      footStyles: { fillColor: [241, 245, 249], textColor: 15, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 40, right: 40 },
    });
    cursorY = doc.lastAutoTable.finalY + 18;
  });

  // Footer page numbers
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(
      `Page ${i} of ${pageCount}  -  Generated by AXIORA`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 20,
      { align: "center" }
    );
  }

  doc.save(`${filename}-${today()}.pdf`);
  } catch (err) {
    devError("PDF export failed", err);
    toast.error(`PDF export failed: ${err?.message || err}`);
  }
}

export function downloadJSON(filename, obj) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
  triggerDownload(blob, `${filename}-${today()}.json`);
}

/**
 * Replace characters jsPDF's default Helvetica (WinAnsi) cannot render.
 * Without this, em-dashes, smart quotes and arrows turn into "!'" garbage.
 */
const _sanitizeForPdf = (s) => {
  if (s == null) return "";
  return String(s)
    .replace(/[\u2018\u2019\u201A]/g, "'")
    .replace(/[\u201C\u201D\u201E]/g, '"')
    .replace(/\u2013|\u2014/g, "-")
    .replace(/\u2192|\u2794|\u279C/g, "to")
    .replace(/\u2212/g, "-")
    .replace(/\u20B9/g, "Rs.")           // ₹ → Rs.  (Helvetica has no Rupee glyph)
    .replace(/[\u00A0]/g, " ");
};

/**
 * Export a "presentation-grade" financial statement PDF.
 * Designed for Balance Sheet, Profit & Loss, Cash Flow.
 *
 * sections: [{
 *   title:    "Equity and Liabilities",        // big section heading
 *   headers:  ["#", "Particulars", "Amount (₹)"],
 *   rows:     [["I", "Owners' Funds", ""], ...] // each row can have flags:
 *     // 3rd-element forms: string (rendered as-is), or object {value, style}
 *     // shorthand: a row can also be { cells: [...], type: "group"|"sub"|"ledger"|"total"|"grand" }
 *   total:    { label: "TOTAL — Equity and Liabilities", amount: "12,34,567" }  // optional
 * }]
 *
 * Style classes per row:
 *   - "group"   = top-level group (bold, light bg)
 *   - "sub"     = subgroup line (normal, light)
 *   - "ledger"  = drill-down ledger line (small, indented, italic-ish)
 *   - "total"   = subtotal (bold + top border)
 *   - "grand"   = section grand total (white-on-blue strip)
 */
export function exportFinancialPDF(filename, title, sections, meta = {}) {
  try {
    const orient = meta.orientation || "portrait";
    const doc = new jsPDF({ orientation: orient, unit: "pt", format: "a4" });
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();

    // ---------- Header ----------
    doc.setFillColor(29, 78, 216); // blue-700
    doc.rect(0, 0, pw, 6, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(15, 23, 42);
    const companyClean = _sanitizeForPdf(meta.company || "AXIORA");
    doc.text(companyClean, 40, 36);

    // Address can be multi-line. Split on newlines and render up to 3 lines.
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(71, 85, 105);
    let addrY = 52;
    if (meta.address) {
      const addrLines = _sanitizeForPdf(meta.address).split(/\r?\n/).filter(Boolean).slice(0, 3);
      addrLines.forEach((line) => {
        doc.text(line, 40, addrY);
        addrY += 12;
      });
    }
    // Contact info on the same block — phone / email below address.
    const contactBits = [meta.phone, meta.email].filter(Boolean);
    if (contactBits.length) {
      doc.text(_sanitizeForPdf(contactBits.join("  •  ")), 40, addrY);
      addrY += 12;
    }
    if (meta.gstin) {
      doc.setTextColor(29, 78, 216);
      doc.setFont("helvetica", "bold");
      doc.text(`GSTIN: ${_sanitizeForPdf(meta.gstin)}${meta.pan ? `   PAN: ${_sanitizeForPdf(meta.pan)}` : ""}`, 40, addrY);
      addrY += 14;
      doc.setFont("helvetica", "normal");
      doc.setTextColor(71, 85, 105);
    }

    // Right-side metadata
    doc.setFontSize(9);
    doc.setTextColor(100);
    const rightLines = [
      `Generated: ${new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}`,
      meta.fy ? `Financial Year: ${_sanitizeForPdf(meta.fy)}` : null,
      "Amounts in Indian Rupees (Rs.)",
    ].filter(Boolean);
    rightLines.forEach((l, i) => doc.text(l, pw - 40, 36 + i * 12, { align: "right" }));

    // Title strip — dynamically positioned below the (variable-height) header.
    const stripTop = Math.max(addrY + 6, 92);
    doc.setFillColor(241, 245, 249);
    doc.rect(40, stripTop, pw - 80, 30, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(15, 23, 42);
    doc.text(_sanitizeForPdf(title), pw / 2, stripTop + 19, { align: "center" });
    if (meta.subtitle) {
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(71, 85, 105);
      doc.text(_sanitizeForPdf(meta.subtitle), pw / 2, stripTop + 39, { align: "center" });
    }

    let cursorY = stripTop + (meta.subtitle ? 50 : 40);

    sections.forEach((sec) => {
      // Section sub-heading
      if (sec.title) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(29, 78, 216);
        doc.text(_sanitizeForPdf(sec.title), 40, cursorY);
        cursorY += 4;
      }

      const styled = (sec.rows || []).map((r) => {
        if (Array.isArray(r)) return { cells: r, type: "plain" };
        return r;
      });

      // Sanitise every cell — protects against Unicode garbage in the body.
      const cleanCells = (cells) => cells.map((c) => (c == null ? "" : _sanitizeForPdf(c)));

      autoTable(doc, {
        startY: cursorY,
        head: [cleanCells(sec.headers)],
        body: styled.map((r) => cleanCells(r.cells)),
        styles: { fontSize: 9, cellPadding: 5, lineColor: [226, 232, 240], lineWidth: 0.4 },
        headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: "bold", halign: "left" },
        columnStyles: { [sec.headers.length - 1]: { halign: "right", font: "courier" } },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        margin: { left: 40, right: 40 },
        didParseCell: (data) => {
          if (data.section !== "body") return;
          const r = styled[data.row.index];
          if (!r) return;
          if (r.type === "group") {
            data.cell.styles.fillColor = [219, 234, 254]; // blue-100
            data.cell.styles.textColor = [15, 23, 42];
            data.cell.styles.fontStyle = "bold";
          } else if (r.type === "sub") {
            data.cell.styles.fillColor = [248, 250, 252];
            data.cell.styles.fontStyle = "bold";
          } else if (r.type === "ledger") {
            data.cell.styles.fillColor = [255, 255, 255];
            data.cell.styles.textColor = [71, 85, 105];
            data.cell.styles.fontSize = 8;
            if (data.column.index === 1) {
              data.cell.styles.cellPadding = { top: 3, bottom: 3, left: 22, right: 5 };
            }
          } else if (r.type === "total") {
            data.cell.styles.fillColor = [241, 245, 249];
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.lineWidth = { top: 1.2, bottom: 0.4, left: 0.4, right: 0.4 };
            data.cell.styles.lineColor = [15, 23, 42];
          } else if (r.type === "grand") {
            data.cell.styles.fillColor = [29, 78, 216];
            data.cell.styles.textColor = [255, 255, 255];
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fontSize = 10;
          }
        },
      });
      cursorY = doc.lastAutoTable.finalY + 16;
    });

    // ---------- Signature block (for BS / P&L / CF) ----------
    if (meta.showSignatures !== false) {
      if (cursorY > ph - 100) { doc.addPage(); cursorY = 60; }
      const signY = Math.max(cursorY + 30, ph - 100);
      doc.setFontSize(9);
      doc.setTextColor(71, 85, 105);
      doc.setFont("helvetica", "italic");
      doc.text("The accompanying notes are an integral part of these financial statements.", 40, signY - 14);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(15, 23, 42);

      const col1X = 40, col2X = pw / 2 + 20;
      doc.line(col1X, signY + 30, col1X + 160, signY + 30);
      doc.line(col2X, signY + 30, col2X + 160, signY + 30);
      doc.setFontSize(8);
      doc.text(`For ${_sanitizeForPdf(meta.company || "the entity")}`, col1X, signY + 44);
      doc.text("As per our report of even date", col2X, signY + 44);
      doc.text("Proprietor / Partner / Director", col1X, signY + 56);
      doc.text("For (Auditors' Firm)", col2X, signY + 56);
      doc.setFontSize(7);
      doc.setTextColor(100);
      doc.text(`Place: ${_sanitizeForPdf(meta.place || "________")}    Date: ${meta.date || today()}`, col1X, signY + 72);
    }

    // ---------- Footer ----------
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setDrawColor(226, 232, 240);
      doc.line(40, ph - 30, pw - 40, ph - 30);
      doc.setFontSize(7);
      doc.setTextColor(120);
      doc.text(`${_sanitizeForPdf(meta.company || "AXIORA")}  -  ${_sanitizeForPdf(title)}`, 40, ph - 18);
      doc.text(`Page ${i} of ${pageCount}`, pw - 40, ph - 18, { align: "right" });
    }

    doc.save(`${filename}-${today()}.pdf`);
  } catch (err) {
    devError("Financial PDF export failed", err);
    toast.error(`PDF export failed: ${err?.message || err}`);
  }
}

/**
 * Build a styled-Excel workbook for financial statements.
 * Each sheet: rows where each row can be either an array OR { cells, type }.
 * Applies basic column widths, header styling, currency formatting on the last column.
 */
export function exportFinancialExcel(filename, sheets, meta = {}) {
  try {
    const wb = XLSX.utils.book_new();
    sheets.forEach((s) => {
      const flatRows = [];
      // Banner rows
      if (meta.company) flatRows.push([meta.company]);
      if (s.title)      flatRows.push([s.title]);
      if (meta.subtitle) flatRows.push([meta.subtitle]);
      flatRows.push([]);
      if (s.headers) flatRows.push(s.headers);

      (s.rows || []).forEach((r) => {
        const cells = Array.isArray(r) ? r : r.cells;
        flatRows.push(cells);
      });
      const ws = XLSX.utils.aoa_to_sheet(flatRows);

      // Column widths
      const colCount = Math.max(...flatRows.map((r) => (r || []).length));
      ws["!cols"] = Array.from({ length: colCount }, (_, i) => {
        if (i === 0) return { wch: 6 };
        if (i === colCount - 1) return { wch: 22 };
        if (i === 1) return { wch: 60 };
        return { wch: 14 };
      });
      // Merge banner rows
      ws["!merges"] = ws["!merges"] || [];
      let r = 0;
      if (meta.company) { ws["!merges"].push({ s: { r, c: 0 }, e: { r, c: colCount - 1 } }); r++; }
      if (s.title)      { ws["!merges"].push({ s: { r, c: 0 }, e: { r, c: colCount - 1 } }); r++; }
      if (meta.subtitle) { ws["!merges"].push({ s: { r, c: 0 }, e: { r, c: colCount - 1 } }); r++; }

      const name = (s.name || "Sheet").replace(/[\\/?*[\]:]/g, " ").slice(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, name);
    });

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    triggerDownload(blob, `${filename}-${today()}.xlsx`);
  } catch (err) {
    devError("Financial Excel export failed", err);
    toast.error(`Excel export failed: ${err?.message || err}`);
  }
}
